import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useHttp from "../../hooks/http.hook";
import "../business/Business.css";
import "../../app/dashboard/Dashboard.css";
import "./OrderDetailPage.css";
import CreateBusinessHeader from "../business/components/CreateBusinessHeader";
import DeleteProductModal from "../../app/dashboard/components/product_item_components/DeleteProductModal";
import EditStockMovementModal from "./components/EditStockMovementModal";
import OrderInfoSection from "./components/OrderInfoSection";
import OrderProductsSection, { isShipmentMovement } from "./components/OrderProductsSection";
import OrderActionsSection from "./components/OrderActionsSection";
import OrderFilterSelectModal from "../../app/orders/components/OrderFilterSelectModal";
import CancelOrderConfirmModal from "../../app/orders/components/CancelOrderConfirmModal";
import PaymentStatusSelectModal from "../../app/orders/components/PaymentStatusSelectModal";
import { PaymentStatusTypes } from "../../types/PaymentStatusTypes";
import {
  OrderStatusTypes,
  buildStatusFilterOptions,
  sortCustomOrderStatuses,
} from "../../types/OrderStatusTypes";
import { getOrderTypeId, isExpenseOrder, isInvestmentOrder, isReplenishOrder } from "../../types/OrderTypes";

function OrderDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { branchId, orderId, tgChannelId } = useParams();
  const { loading: httpLoading, requestWithMeta } = useHttp();

  const [order, setOrder] = React.useState(null);
  const [stockMovements, setStockMovements] = React.useState([]);
  const [payments, setPayments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [paymentError, setPaymentError] = React.useState(null);
  const [paymentSubmitting, setPaymentSubmitting] = React.useState(false);
  const [editingMovement, setEditingMovement] = React.useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState(null);
  const [customStatuses, setCustomStatuses] = React.useState([]);
  const [statusesReady, setStatusesReady] = React.useState(false);
  const [statusModalOpen, setStatusModalOpen] = React.useState(false);
  const [statusError, setStatusError] = React.useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = React.useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setStatusesReady(false);
    (async () => {
      try {
        const res = await requestWithMeta("/operator/custom-order-statuses", "GET");
        if (cancelled) return;
        const list = Array.isArray(res?.data?.customOrderStatuses)
          ? res.data.customOrderStatuses
          : [];
        setCustomStatuses(sortCustomOrderStatuses(list));
      } catch (_) {
        if (!cancelled) setCustomStatuses([]);
      } finally {
        if (!cancelled) setStatusesReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requestWithMeta]);

  React.useEffect(() => {
    if (!orderId) {
      navigate(
        `/operator/${encodeURIComponent(tgChannelId)}/branch/store`,
        { replace: true }
      );
      return;
    }
    if (!statusesReady) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await requestWithMeta(
          `/operator/orders/${encodeURIComponent(orderId)}`,
          "GET"
        );

        if (cancelled) return;

        if (!res?.ok) {
          setError(
            res?.data?.error || res?.data?.message || "Не удалось загрузить заказ"
          );
          setOrder(null);
          setStockMovements([]);
          setPayments([]);
          return;
        }

        setOrder(res?.data?.order ?? null);
        setStockMovements(
          Array.isArray(res?.data?.stockMovements) ? res.data.stockMovements : []
        );
        setPayments(Array.isArray(res?.data?.payments) ? res.data.payments : []);
      } catch (_) {
        if (!cancelled) {
          setError("Ошибка загрузки");
          setOrder(null);
          setStockMovements([]);
          setPayments([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orderId, requestWithMeta, navigate, tgChannelId, statusesReady]);

  const handleBack = () => {
    if (branchId && tgChannelId) {
      navigate({
        pathname: `/operator/${encodeURIComponent(tgChannelId)}/branch/${encodeURIComponent(branchId)}`,
        search: location.search,
      });
    } else if (tgChannelId) {
      navigate(`/operator/${encodeURIComponent(tgChannelId)}/branch/store`);
    }
  };

  const handleStockMovementSaved = (updated) => {
    if (!updated) return;
    const id = updated?.id ?? updated?._id;
    setStockMovements((prev) =>
      prev.map((m) => ((m?.id ?? m?._id) === id ? { ...m, ...updated } : m))
    );
    setEditingMovement(null);
  };

  const handleStatusSelect = (nextStatusId) => {
    if (nextStatusId == null) return;
    const currentStatusId = order?.statusId ?? order?.status_id;
    if (
      Number(nextStatusId) === OrderStatusTypes.CANCELED &&
      Number(currentStatusId) !== OrderStatusTypes.CANCELED
    ) {
      setCancelConfirmOpen(true);
      return;
    }
    handleStatusChange(nextStatusId);
  };

  const handleStatusChange = async (newStatusId) => {
    const previousStatusId = order?.statusId ?? order?.status_id;
    setStatusError(null);
    try {
      setOrder((prev) => ({ ...prev, statusId: null, status_id: null }));
      const res = await requestWithMeta(
        `/operator/orders/${encodeURIComponent(orderId)}/status`,
        "PUT",
        { statusId: newStatusId }
      );
      if (res?.ok) {
        setOrder((prev) => ({ ...prev, statusId: newStatusId, status_id: newStatusId }));
        return;
      }
      setOrder((prev) => ({ ...prev, statusId: previousStatusId, status_id: previousStatusId }));
      setStatusError(res?.data?.error || res?.data?.message || "Не удалось изменить статус");
    } catch (_) {
      setOrder((prev) => ({ ...prev, statusId: previousStatusId, status_id: previousStatusId }));
      setStatusError("Не удалось изменить статус");
    }
  };

  const handlePaymentAction = async (body) => {
    const previousPaymentStatusId = order?.paymentStatusId ?? order?.payment_status_id;
    const previousDebt = order?.debt;
    const previousPayments = payments;
    setPaymentError(null);
    setPaymentSubmitting(true);
    try {
      setOrder((prev) => ({ ...prev, paymentStatusId: null, payment_status_id: null }));
      const res = await requestWithMeta(
        `/operator/orders/${encodeURIComponent(orderId)}/payment-status`,
        "PUT",
        body
      );
      if (res?.ok) {
        const nextPaymentStatusId = res?.data?.paymentStatusId ?? previousPaymentStatusId;
        const nextPayments = Array.isArray(res?.data?.payments) ? res.data.payments : previousPayments;
        setPayments(nextPayments);
        setOrder((prev) => ({
          ...prev,
          paymentStatusId: nextPaymentStatusId,
          payment_status_id: nextPaymentStatusId,
          debt: res?.data?.debt ?? prev?.debt,
        }));
        return true;
      }
      setPaymentError(res?.data?.error || res?.data?.message || "Не удалось изменить оплату");
      setPayments(previousPayments);
      setOrder((prev) => ({
        ...prev,
        paymentStatusId: previousPaymentStatusId,
        payment_status_id: previousPaymentStatusId,
        debt: previousDebt,
      }));
      return false;
    } catch (_) {
      setPaymentError("Ошибка изменения оплаты");
      setPayments(previousPayments);
      setOrder((prev) => ({
        ...prev,
        paymentStatusId: previousPaymentStatusId,
        payment_status_id: previousPaymentStatusId,
        debt: previousDebt,
      }));
      return false;
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const handleAskDelete = () => {
    setDeleteError(null);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteError(null);
    try {
      const res = await requestWithMeta(
        `/operator/orders/${encodeURIComponent(orderId)}`,
        "DELETE"
      );
      if (res?.ok) {
        setDeleteConfirmOpen(false);
        handleBack();
        return;
      }
      setDeleteError(
        res?.data?.error || res?.data?.message || "Не удалось удалить запись"
      );
    } catch (_) {
      setDeleteError("Ошибка удаления");
    }
  };

  if (loading) {
    return (
      <div className="order-detail-root">
        <CreateBusinessHeader title="Заказ" subtitle="" onBack={handleBack} />
        <main className="order-detail-content">
          <div className="order-detail-skeleton order-detail-skeleton--block" />
          <div className="order-detail-skeleton order-detail-skeleton--block" />
          <div className="order-detail-skeleton order-detail-skeleton--block" />
        </main>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-detail-root">
        <CreateBusinessHeader title="Заказ" subtitle="" onBack={handleBack} />
        <main className="order-detail-content">
          <div className="order-detail-error">{error || "Заказ не найден"}</div>
        </main>
      </div>
    );
  }

  const id = order?.id ?? order?._id;
  const statusId = order?.statusId ?? order?.status_id;
  const typeId = getOrderTypeId(order);
  const paymentStatusId = order?.paymentStatusId ?? order?.payment_status_id;
  const orderTotal = stockMovements.reduce((sum, m) => {
    const qty = m?.quantity ?? 0;
    const p = m?.productPrice ?? m?.product_price ?? 0;
    const price = typeof p === "number" ? p : parseFloat(p) || 0;
    return sum + qty * price;
  }, 0);
  const currentDebtRaw = order?.debt;
  const currentDebt = typeof currentDebtRaw === "number" ? currentDebtRaw : parseFloat(currentDebtRaw) || 0;
  const isReplenishment = isReplenishOrder(order);
  const isExpense = isExpenseOrder(order);
  const isInvestment = isInvestmentOrder(order);
  const isCanceledUnpaid =
    !isReplenishment &&
    !isExpense &&
    !isInvestment &&
    Number(statusId) === OrderStatusTypes.CANCELED &&
    Number(paymentStatusId ?? PaymentStatusTypes.NOT_PAID) === PaymentStatusTypes.NOT_PAID;
  const statusLabel = isReplenishment
    ? `Пополнение № ${id}`
    : isExpense
      ? `Расход № ${id}`
      : isInvestment
        ? `Инвестиция № ${id}`
        : `Заказ № ${id}`;

  return (
    <div className="order-detail-root">
      <CreateBusinessHeader title={statusLabel} subtitle="" onBack={handleBack} />

      <main className="order-detail-content">
        <OrderInfoSection
          order={order}
          isSystemOrder={isReplenishment || isExpense || isInvestment}
          onPaymentStatusOpen={() => setPaymentModalOpen(true)}
          statusError={statusError}
          customStatuses={customStatuses}
        />

        <OrderProductsSection
          stockMovements={stockMovements}
          isReplenishment={isReplenishment}
          hideQuantity={isExpense || isInvestment}
          onEditMovement={setEditingMovement}
        />
      </main>

      <OrderActionsSection
        typeId={typeId}
        showDelete={isCanceledUnpaid}
        onStatusOpen={() => setStatusModalOpen(true)}
        onDelete={handleAskDelete}
        statusDisabled={statusId == null}
      />

      <OrderFilterSelectModal
        isOpen={statusModalOpen}
        title="Статус"
        options={buildStatusFilterOptions(customStatuses)}
        selectedIds={statusId == null ? [] : [Number(statusId)]}
        colorizeStatus
        onApply={(ids) => handleStatusSelect(ids[0])}
        onClose={() => setStatusModalOpen(false)}
      />

      <CancelOrderConfirmModal
        isOpen={cancelConfirmOpen}
        orderLabel={statusLabel}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          setCancelConfirmOpen(false);
          handleStatusChange(OrderStatusTypes.CANCELED);
        }}
      />

      <PaymentStatusSelectModal
        isOpen={paymentModalOpen}
        payments={payments}
        currentDebt={currentDebt}
        totalPrice={orderTotal}
        submitting={paymentSubmitting}
        error={paymentError}
        onApply={handlePaymentAction}
        onClose={() => {
          if (paymentSubmitting) return;
          setPaymentModalOpen(false);
          setPaymentError(null);
        }}
      />

      {editingMovement ? (
        <EditStockMovementModal
          movement={editingMovement}
          showPrice={!isReplenishment}
          hideQuantity={isExpense || isInvestment || isShipmentMovement(editingMovement)}
          onClose={() => setEditingMovement(null)}
          onSaved={handleStockMovementSaved}
          requestWithMeta={requestWithMeta}
        />
      ) : null}

      <DeleteProductModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          if (httpLoading) return;
          setDeleteConfirmOpen(false);
          setDeleteError(null);
        }}
        title={
          isReplenishment
            ? "Удалить пополнение"
            : isExpense
              ? "Удалить расход"
              : isInvestment
                ? "Удалить инвестицию"
                : "Удалить заказ"
        }
        productName={statusLabel}
        deleteError={deleteError}
        loading={httpLoading}
        onConfirmDelete={handleConfirmDelete}
      />
    </div>
  );
}

export default OrderDetailPage;
